import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { ActionIcon, type ActionIconProps, Menu, Tooltip } from '@mantine/core';
import {
  MRT_Column,
  MRT_RowData,
  MRT_TableInstance,
  MRT_ShowHideColumnsMenuItems,
} from 'mantine-react-table';
import classes from './ClaimsShowHideColumnsButton.module.css';

interface ClaimsShowHideColumnsButtonProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
  title?: string;
}

interface ColumnWithHeader {
  id: string;
  columnDef: {
    header?: unknown;
  };
}

const columnLabelCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const getColumnLabel = (column: ColumnWithHeader): string =>
  typeof column.columnDef.header === 'string'
    ? column.columnDef.header
    : column.id;

export const sortColumnsForShowHideMenu = <TColumn extends ColumnWithHeader>(
  columns: TColumn[]
): TColumn[] =>
  [...columns].sort((left, right) =>
    columnLabelCollator.compare(getColumnLabel(left), getColumnLabel(right))
  );

export const ClaimsShowHideColumnsButton = <TData extends MRT_RowData>({
  table,
  title,
  className,
  ...actionIconProps
}: ClaimsShowHideColumnsButtonProps<TData> & ActionIconProps) => {
  const {
    getAllColumns,
    getCenterLeafColumns,
    getLeftLeafColumns,
    getRightLeafColumns,
  } = table;
  const {
    icons: { IconColumns },
    localization: { showHideColumns },
  } = table.options;
  const { columnOrder } = table.getState();

  const allColumns = useMemo(() => {
    const columns = getAllColumns();
    if (
      columnOrder.length > 0 &&
      !columns.some((col) => col.columnDef.columnDefType === 'group')
    ) {
      const orderedColumns = [
        ...getLeftLeafColumns(),
        ...Array.from(new Set(columnOrder)).map((colId) =>
          getCenterLeafColumns().find((col) => col?.id === colId)
        ),
        ...getRightLeafColumns(),
      ].filter((column): column is MRT_Column<TData> => Boolean(column));

      return sortColumnsForShowHideMenu(orderedColumns);
    }

    return sortColumnsForShowHideMenu(columns);
  }, [
    columnOrder,
    getAllColumns,
    getCenterLeafColumns,
    getLeftLeafColumns,
    getRightLeafColumns,
  ]);

  const [hoveredColumn, setHoveredColumn] = useState<MRT_Column<TData> | null>(
    null
  );

  return (
    <Menu
      closeOnItemClick={false}
      middlewares={{
        flip: false,
        shift: { padding: 8 },
        size: { padding: 8 },
      }}
      position="bottom-end"
      withinPortal
    >
      <Tooltip label={title ?? showHideColumns} withinPortal>
        <Menu.Target>
          <ActionIcon
            aria-label={title ?? showHideColumns}
            color="gray"
            className={className}
            size="lg"
            variant="subtle"
            {...actionIconProps}
          >
            <IconColumns />
          </ActionIcon>
        </Menu.Target>
      </Tooltip>
      <Menu.Dropdown
        className={clsx('mrt-show-hide-columns-menu', classes.root)}
      >
        {allColumns.map((column, index) => (
          <MRT_ShowHideColumnsMenuItems
            allColumns={allColumns}
            column={column}
            hoveredColumn={hoveredColumn}
            key={`${index}-${column.id}`}
            setHoveredColumn={setHoveredColumn}
            table={table}
          />
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};
